import { useEffect, useMemo, useState } from "react";

import { useQuery } from "@tanstack/react-query";

import { initUseQuery } from "simplydo/core/query";
import { useThrottle } from "simplydo/react/hooks";
import { styled } from "simplydo/react/styled";
import { Button, Header, Icon, Message } from "simplydo/react/ui";
import { Input } from "simplydo/react/ui";

import api from "api";
import util from "utils/utils";

import { UserChip } from "components/lib/Chips";
import BaseChip from "components/lib/Chips/BaseChip";
import type { ChipUser } from "components/lib/Chips/types";

import type { AssignableUser } from "./AssignmentDashboard";

type ReassignAssessmentsPanelProps = {
  canReassign: boolean;
  reassignMode: boolean;
  selectedCount: number;
  challengeId: string;
  excludeUserIds?: string[];
  targetAssessor: AssignableUser | null;
  onTargetAssessorChange: (assessor: AssignableUser | null) => void;
  onStartReassign: () => void;
  onConfirm: () => void;
  onCancel: () => void;
};

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
`;

const SectionLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #6b7280;
`;

const TargetUserRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 6px;
  background: #fff;
  border: 1px solid #e3e6eb;
`;

const UserResults = styled.div`
  max-height: 180px;
  overflow-y: auto;
  border: 1px solid #e3e6eb;
  border-radius: 6px;
  background: #fff;
`;

const UserResultRow = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 8px 10px;
  border: none;
  border-bottom: 1px solid #f0f1f3;
  background: transparent;
  cursor: pointer;
  text-align: left;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f5f7fa;
  }
`;

const ActionsBar = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid #e3e6eb;
`;

export const ReassignAssessmentsPanel = ({
  canReassign,
  reassignMode,
  selectedCount,
  challengeId,
  excludeUserIds = [],
  targetAssessor,
  onTargetAssessorChange,
  onStartReassign,
  onConfirm,
  onCancel,
}: ReassignAssessmentsPanelProps) => {
  const [assessorSearchQuery, setAssessorSearchQuery] = useState("");

  useEffect(() => {
    if (!reassignMode) {
      setAssessorSearchQuery("");
    }
  }, [reassignMode]);

  const throttlePotentialAssessorUsers = useThrottle(
    api.challenges.getPotentialAssessorUsers,
    400,
    [],
  );
  const throttlePotentialAssessorEmails = useThrottle(
    api.challenges.getPotentialAssessorEmails,
    400,
    [],
  );

  const searchEnabled = !!challengeId && reassignMode && !targetAssessor;

  const potentialAssessorUsersQuery = useQuery(
    initUseQuery(
      throttlePotentialAssessorUsers,
      [challengeId, { query: assessorSearchQuery, page: 1 }],
      { isEnabled: searchEnabled },
    ),
  );
  const potentialAssessorEmailsQuery = useQuery(
    initUseQuery(
      throttlePotentialAssessorEmails,
      [challengeId, { query: assessorSearchQuery, page: 1 }],
      { isEnabled: searchEnabled },
    ),
  );

  const exclude = useMemo(() => new Set(excludeUserIds), [excludeUserIds]);

  const potentialAssessorResults = useMemo(() => {
    const results: AssignableUser[] = [];

    for (const user of potentialAssessorUsersQuery.data?.ownerUsers || []) {
      if (!exclude.has(user._id)) {
        results.push({
          _id: user._id,
          isEmailInvitee: false,
          ownerAssessor: { _id: user._id, profile: user.profile },
          assignments: {},
        } as AssignableUser);
      }
    }

    for (const email of potentialAssessorEmailsQuery.data?.ownerEmails || []) {
      if (email._id && !exclude.has(email._id)) {
        results.push({
          _id: email._id,
          email: email._id,
          isEmailInvitee: true,
          assignments: {},
        } as AssignableUser);
      }
    }

    return results;
  }, [
    potentialAssessorUsersQuery.data?.ownerUsers,
    potentialAssessorEmailsQuery.data?.ownerEmails,
    exclude,
  ]);

  const isSearching =
    potentialAssessorUsersQuery.isFetching ||
    potentialAssessorEmailsQuery.isFetching;

  const canConfirm = selectedCount > 0 && !!targetAssessor;

  if (!canReassign) {
    return null;
  }

  if (!reassignMode) {
    return (
      <div style={{ marginTop: 30 }}>
        <Header as="h4" style={{ marginBottom: 0 }}>
          Reassign assessments
        </Header>
        <p>
          Using reassignment will instantly remove the assessments from the
          current assessor and assign them to the newly selected assessor. You
          can cancel the reassignment at any time.
        </p>
        <Button
          size="small"
          icon="exchange"
          content="Start reassignment"
          onClick={onStartReassign}
        />
      </div>
    );
  }

  return (
    <div>
      <h4 style={{ marginBottom: 0 }}>Reassign assessments</h4>
      <p>
        Select the assessments you want to reassign by clicking them above, then
        choose a new assessor to assign them to.
      </p>
      <Section>
        <SectionLabel>Step 1 · Select assessments</SectionLabel>
        <Message success={selectedCount > 0} style={{ margin: 0 }}>
          <Icon
            name={selectedCount > 0 ? "check circle" : "check circle outline"}
          />
          {selectedCount > 0
            ? `${util.pluralise(selectedCount, "assessment", "assessments")} selected`
            : "No assessments selected yet"}
        </Message>
      </Section>
      <Section>
        <SectionLabel>Step 2 · Select new assessor</SectionLabel>
        {targetAssessor ? (
          <TargetUserRow>
            {targetAssessor.isEmailInvitee ? (
              <BaseChip
                icon="mail"
                title={targetAssessor.email}
                compact
                content={`This is a pending invitation, the user has not yet signed up to ${util.appName()}.`}
              />
            ) : (
              <UserChip
                user={targetAssessor.ownerAssessor as ChipUser}
                compact
              />
            )}
            <Button
              basic
              size="mini"
              content="Choose different assessor"
              onClick={() => onTargetAssessorChange(null)}
            />
          </TargetUserRow>
        ) : null}
        {!targetAssessor ? (
          <Input
            fluid
            icon="search"
            iconPosition="left"
            loading={isSearching}
            placeholder="Search potential assessors..."
            value={assessorSearchQuery}
            onChange={(_e, { value }) => setAssessorSearchQuery(String(value))}
          />
        ) : null}
        {!targetAssessor && potentialAssessorResults.length > 0 ? (
          <UserResults>
            {potentialAssessorResults.map((assessor) => (
              <UserResultRow
                key={assessor._id}
                type="button"
                onClick={() => {
                  onTargetAssessorChange(assessor);
                  setAssessorSearchQuery("");
                }}
              >
                {assessor.isEmailInvitee ? (
                  <BaseChip
                    icon="mail"
                    title={assessor.email}
                    compact
                    content={`This is a pending invitation, the user has not yet signed up to ${util.appName()}.`}
                  />
                ) : (
                  <UserChip user={assessor.ownerAssessor as ChipUser} compact />
                )}
              </UserResultRow>
            ))}
          </UserResults>
        ) : null}
        {!targetAssessor &&
        assessorSearchQuery &&
        !isSearching &&
        potentialAssessorResults.length === 0 ? (
          <Message size="small">No matching assessors found.</Message>
        ) : null}
      </Section>
      <ActionsBar>
        <Button size="small" content="Cancel" onClick={onCancel} />
        <Button
          primary
          size="small"
          icon="check"
          content="Confirm reassignments"
          disabled={!canConfirm}
          onClick={onConfirm}
        />
      </ActionsBar>
    </div>
  );
};
